import React, { useState, useEffect } from 'react';
import { Tab, Loader, Message, Button, Modal, Icon, Dropdown, Segment, Popup } from 'semantic-ui-react';
import Chat from '../../../Chat/Chat';

const AISideTabs = ({ is_user_signed_in, current_locale, experiment_id, available_chats_count = 0, allow_chat = true, is_paid_subscriber = false }) => {
    const [chatHistory, setChatHistory] = useState(() => {
        if (window.chatHistory && window.chatHistory[experiment_id]) {
            return window.chatHistory[experiment_id];
        }
        return [];
    });
    const [isLoading, setIsLoading] = useState(available_chats_count > 0 && (!window.chatHistory || !window.chatHistory[experiment_id]));
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showBanner, setShowBanner] = useState(true);

    const translations = {
        deleteButton: {
            en: 'Delete All Messages',
            ar: 'حذف جميع الرسائل',
            tr: 'Tüm Mesajları Sil',
            de: 'Alle Nachrichten löschen',
            es: 'Borrar todos los mensajes',
            fr: 'Supprimer tous les messages'
        },
        aiAssistant: {
            en: 'AI Assistant',
            ar: 'المساعد الذكي',
            tr: 'Yapay Zeka Asistanı',
            de: 'KI-Assistent',
            es: 'Asistente de IA',
            fr: 'Assistant IA'
        },
        upgradeMessage: {
            en: 'You are using GPT-3.5. Upgrade to Premium for GPT-4',
            ar: 'أنت تستخدم GPT-3.5. قم بالترقية إلى Premium للحصول على GPT-4',
            tr: "GPT-3.5 kullanıyorsunuz. GPT-4 için Premium'a yükseltin",
            de: 'Sie verwenden GPT-3.5. Upgrade auf Premium für GPT-4',
            es: 'Estás usando GPT-3.5. Actualiza a Premium para GPT-4',
            fr: 'Vous utilisez GPT-3.5. Passez à Premium pour GPT-4'
        },
        upgrade: {
            en: 'Upgrade',
            ar: 'ترقية',
            tr: 'Yükselt',
            de: 'Upgrade',
            es: 'Actualizar',
            fr: 'Améliorer'
        },
        introMessage: {
            en: 'Welcome to DCACLab AI Assistant! I\'m here to help you with digital circuit design and analysis.',
            ar: 'مرحباً بك في مساعد DCACLab للذكاء الاصطناعي! أنا هنا لمساعدتك في تصميم وتحليل الدوائر الرقمية.',
            tr: 'DCACLab AI Asistanına hoş geldiniz! Dijital devre tasarımı ve analizi konusunda size yardımcı olmak için buradayım.',
            de: 'Willkommen beim DCACLab KI-Assistenten! Ich bin hier, um Ihnen bei der digitalen Schaltungsgestaltung und -analyse zu helfen.',
            es: '¡Bienvenido al Asistente de IA de DCACLab! Estoy aquí para ayudarte con el diseño y análisis de circuitos digitales.',
            fr: 'Bienvenue dans l\'Assistant IA DCACLab ! Je suis là pour vous aider dans la conception et l\'analyse de circuits numériques.'
        }
    };

    const loadingMessages = {
        en: 'Loading previous chat messages...',
        ar: 'جارٍ تحميل الرسائل السابقة...',
        tr: 'Önceki sohbet mesajları yükleniyor...',
        de: 'Vorherige Chatnachrichten werden geladen...',
        es: 'Cargando mensajes de chat anteriores...',
        fr: 'Chargement des messages de chat précédents...'
    };

    useEffect(() => {
        if (!window.chatHistory) {
            window.chatHistory = {};
        }

        if (experiment_id && available_chats_count > 0 && !window.chatHistory[experiment_id]) {
            setIsLoading(true);
            fetch(`/api/chat/history?experiment_id=${experiment_id}`)
                .then(response => response.json())
                .then(data => {
                    if (data.messages) {
                        window.chatHistory[experiment_id] = data.messages;
                        setChatHistory(data.messages);
                    }
                })
                .catch(error => {
                    console.error('Error fetching chat history:', error);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [experiment_id, available_chats_count]);

    useEffect(() => {
        const handleChatHistoryUpdate = (event) => {
            if (event.detail.experimentId === experiment_id) {
                const newHistory = window.chatHistory[experiment_id] || [];
                setChatHistory(newHistory);
            }
        };

        window.addEventListener('chatHistoryUpdated', handleChatHistoryUpdate);
        
        // Initial sync with window.chatHistory
        if (window.chatHistory && window.chatHistory[experiment_id]) {
            setChatHistory(window.chatHistory[experiment_id]);
        }

        return () => window.removeEventListener('chatHistoryUpdated', handleChatHistoryUpdate);
    }, [experiment_id]);

    useEffect(() => {
        const checkDeletingStatus = () => {
            // Check if delete operation is in progress
            setIsDeleting(document.querySelector('.ui.button.loading[aria-label*="Delete"]') !== null);
        };

        const observer = new MutationObserver(checkDeletingStatus);
        observer.observe(document.body, { 
            attributes: true, 
            subtree: true, 
            attributeFilter: ['class'] 
        });

        return () => observer.disconnect();
    }, []);

    // Only count messages that are not the intro message
    const userMessageCount = chatHistory.filter(msg => {
        // Skip the intro message which contains the welcome text
        const isIntroMessage = msg.role === 'assistant' && 
            msg.content.includes('Welcome to DCACLab AI Assistant') && 
            msg.content.includes('Digital circuit design and analysis');
        
        // Count all messages except the intro message
        // This includes user messages and non-intro assistant messages
        return !isIntroMessage;
    }).length;

    const renderUpgradeBanner = () => {
        if (false && is_user_signed_in && !is_paid_subscriber && showBanner) {
            const pricingUrl = current_locale === 'en' ? 
                '/pricing-table' : 
                `/${current_locale}/pricing-table`;

            return (
                <Message 
                    warning
                    size="small"
                    style={{
                        margin: '0',
                        borderRadius: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 16px',
                        position: 'sticky',
                        top: 0,
                        zIndex: 100,
                        background: 'linear-gradient(90deg, #fff9e6 0%, #fff5d6 100%)',
                        border: 'none',
                        borderBottom: '1px solid #ffeeba',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}
                >
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flex: 1,
                        gap: '8px'
                    }}>
                        <Icon 
                            name="info circle"
                            style={{
                                color: '#856404',
                                fontSize: '16px'
                            }}
                        />
                        <span style={{ 
                            fontSize: '0.92em',
                            color: '#856404',
                            fontWeight: '500',
                            paddingRight: '24px'
                        }}>
                            {translations.upgradeMessage[current_locale] || translations.upgradeMessage.en}
                        </span>
                    </div>
                    
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '12px'
                    }}>
                        <Button
                            color="yellow"
                            size="tiny"
                            as="a"
                            href={pricingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ 
                                margin: 0,
                                background: 'linear-gradient(90deg, #ffd700 0%, #ffb700 100%)',
                                color: '#856404',
                                fontWeight: '600',
                                padding: '8px 16px',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                border: '1px solid #ffb700',
                                transition: 'all 0.2s ease',
                                ':hover': {
                                    background: 'linear-gradient(90deg, #ffb700 0%, #ffa500 100%)',
                                    transform: 'translateY(-1px)',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                                }
                            }}
                        >
                            {translations.upgrade[current_locale] || translations.upgrade.en}
                        </Button>
                        
                        <Icon 
                            name="close" 
                            style={{
                                cursor: 'pointer',
                                fontSize: '16px',
                                color: '#856404',
                                opacity: 0.6,
                                padding: '8px',
                                marginLeft: '-4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'opacity 0.2s ease',
                                ':hover': {
                                    opacity: 1
                                }
                            }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setShowBanner(false);
                            }}
                            aria-label="Close upgrade banner"
                        />
                    </div>
                </Message>
            );
        }
        return null;
    };

    const panes = [
        {
            menuItem: {
                key: 'ai',
                content: (
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px',
                        position: 'relative',
                        zIndex: 1000
                    }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <img 
                                src="/assets/square-logo.png" 
                                alt={translations.aiAssistant[current_locale] || translations.aiAssistant.en}
                                style={{
                                    width: '24px',
                                    height: '24px',
                                    borderRadius: '4px'
                                }}
                            />
                            {translations.aiAssistant[current_locale] || translations.aiAssistant.en}
                            <Popup
                                trigger={
                                    <Icon 
                                        name="info circle" 
                                        size="small"
                                        style={{ 
                                            opacity: 0.6, 
                                            cursor: 'pointer',
                                            marginLeft: '4px'
                                        }}
                                    />
                                }
                                content={translations.introMessage[current_locale] || translations.introMessage.en}
                                position="bottom left"
                                style={{
                                    maxWidth: '300px',
                                    fontSize: '0.9em',
                                    padding: '0.8em',
                                    lineHeight: '1.4',
                                    zIndex: 1002
                                }}
                            />
                        </span>
                        {userMessageCount > 0 && allow_chat && is_user_signed_in && (
                            <Dropdown
                                icon={null}
                                trigger={
                                    <div 
                                        style={{ 
                                            cursor: 'pointer',
                                            opacity: isDeleting ? 0.5 : 1,
                                            display: 'flex',
                                            gap: '3px',
                                            padding: '4px',
                                            marginLeft: '2px',
                                            position: 'relative',
                                            zIndex: 1000
                                        }}
                                    >
                                        <div style={{
                                            width: '3px',
                                            height: '3px',
                                            backgroundColor: '#666',
                                            borderRadius: '1px'
                                        }} />
                                        <div style={{
                                            width: '3px',
                                            height: '3px',
                                            backgroundColor: '#666',
                                            borderRadius: '1px'
                                        }} />
                                        <div style={{
                                            width: '3px',
                                            height: '3px',
                                            backgroundColor: '#666',
                                            borderRadius: '1px'
                                        }} />
                                    </div>
                                }
                                pointing="top right"
                                direction="left"
                            >
                                <Dropdown.Menu style={{ zIndex: 1001 }}>
                                    <Dropdown.Item
                                        icon="trash"
                                        text={translations.deleteButton[current_locale]}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.handleDeleteMessages && !isDeleting) {
                                                window.handleDeleteMessages();
                                            }
                                        }}
                                        disabled={isDeleting}
                                    />
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </div>
                )
            },
            render: () => (
                <Tab.Pane 
                    attached={false} 
                    style={{ 
                        padding: 0,
                        height: '100%',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {renderUpgradeBanner()}
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '2rem' }}>
                            <Message info>
                                <Loader active inline='centered' />
                                <p style={{ textAlign: 'center', marginTop: '1rem' }}>
                                    {loadingMessages[current_locale] || loadingMessages['en']}
                                </p>
                            </Message>
                        </div>
                    ) : (
                        <div style={{ 
                            flex: 1, 
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                        }}>
                            <Chat 
                                is_user_signed_in={is_user_signed_in}
                                current_locale={current_locale} 
                                experiment_id={experiment_id} 
                                initialMessages={chatHistory}
                                skipIntro={available_chats_count > 0}
                                allow_chat={allow_chat}
                                is_paid_subscriber={is_paid_subscriber}
                            />
                        </div>
                    )}
                </Tab.Pane>
            )
        }
    ];

    return (
        <Tab
            style={{ 
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                right: 0
            }}
            panes={panes}
            menu={{ 
                secondary: true, 
                pointing: true,
                style: { 
                    margin: 0, 
                    borderBottom: '1px solid #e0e0e0',
                    flex: '0 0 auto'
                }
            }}
            renderActiveOnly={true}
        />
    );
};

export default React.memo(AISideTabs);
